(() => {
  "use strict";

  const rootPath = () => {
    const configured = window.Shopify?.routes?.root || "/";
    return configured.endsWith("/") ? configured : `${configured}/`;
  };

  const parseResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) return response.json();
    const text = await response.text();
    return text ? { description: text } : {};
  };

  const bindMenu = (page) => {
    const button = page.querySelector("[data-hmc-menu]");
    const navigation = page.querySelector(".hmc-nav");
    if (!button || !navigation || button.dataset.hmcBound === "true") return;
    button.dataset.hmcBound = "true";

    const close = () => {
      navigation.classList.remove("is-open");
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-label", "Open navigation");
    };

    button.addEventListener("click", () => {
      const open = !navigation.classList.contains("is-open");
      navigation.classList.toggle("is-open", open);
      button.setAttribute("aria-expanded", String(open));
      button.setAttribute("aria-label", open ? "Close navigation" : "Open navigation");
    });

    navigation.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", close);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  };

  const bindForm = (page, form) => {
    if (form.dataset.hmcBound === "true") return;
    form.dataset.hmcBound = "true";

    const purchaseButton = form.querySelector("[data-hmc-submit]");
    const purchaseLabel = form.querySelector("[data-hmc-submit-label]");
    const select = form.querySelector("[data-hmc-variant]");
    const price = page.querySelector("[data-hmc-price]");
    const error = page.querySelector("[data-hmc-error]");
    const linkedButtons = [
      purchaseButton,
      ...page.querySelectorAll(`button[type="submit"][form="${form.id}"]`)
    ].filter(Boolean);
    let loading = false;

    const setError = (message = "") => {
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      if (message) error.focus({ preventScroll: false });
    };

    const setLoading = (active) => {
      loading = active;
      linkedButtons.forEach((button) => {
        button.disabled = active || button.dataset.hmcAvailable === "false";
        button.classList.toggle("is-loading", active);
        button.setAttribute("aria-busy", String(active));
      });
      if (purchaseLabel) {
        purchaseLabel.textContent = active
          ? "Opening checkout..."
          : "Get Hybrid Method";
      }
    };

    const updateVariant = () => {
      if (!select) return;
      const option = select.selectedOptions?.[0];
      if (!option) return;
      const available =
        option.dataset.available === undefined
          ? !option.disabled
          : option.dataset.available === "true";

      linkedButtons.forEach((button) => {
        button.dataset.hmcAvailable = String(available);
        button.disabled = loading || !available;
      });
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (purchaseLabel && !loading) {
        purchaseLabel.textContent = available
          ? "Get Hybrid Method"
          : "Sold out";
      }
    };

    select?.addEventListener("change", updateVariant);
    updateVariant();

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (loading || purchaseButton?.disabled) return;

      setError("");

      if (form.hasAttribute("data-hmc-preview-form")) {
        setError(
          "Mockup only: on Shopify, this button adds The Hybrid Method and opens checkout immediately."
        );
        return;
      }

      setLoading(true);

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: new FormData(form),
          credentials: "same-origin"
        });
        const payload = await parseResponse(response);

        if (!response.ok) {
          throw new Error(
            payload.description ||
              payload.message ||
              "This product could not be added. Check the selected option and try again."
          );
        }

        window.location.assign(`${rootPath()}checkout`);
      } catch (requestError) {
        setError(
          requestError instanceof Error && requestError.message
            ? requestError.message
            : "Checkout could not start. Check your connection and try again."
        );
        setLoading(false);
      }
    });

    form.hmcReset = () => {
      setLoading(false);
      updateVariant();
    };
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll("[data-hmc-page]").forEach((page) => {
      page.querySelectorAll(".hmc-button--header").forEach((button) => {
        if (!button.disabled) button.textContent = "Get Hybrid Method";
      });
      bindMenu(page);
      page.querySelectorAll("[data-hmc-form]").forEach((form) => {
        bindForm(page, form);
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialise(), {
      once: true
    });
  } else {
    initialise();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialise(event.target);
  });
  window.addEventListener("pageshow", () => {
    document.querySelectorAll("[data-hmc-form]").forEach((form) => {
      form.hmcReset?.();
    });
  });
})();
